import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { LayoutDashboard, Bot, Check, X, BarChart3 } from 'lucide-react';
import MessageBubble from '@/components/chat/MessageBubble';
import SegmentRenderer from '@/components/chat/SegmentRenderer';
import { MessageInput } from '@/components/chat';
import { useDashboardBuild } from '@/contexts/DashboardBuildContext';
import type { SystemNotice } from '@/contexts/DashboardBuildContext';
import type { ChatMessage } from '@/types/chat';

type TimelineItem =
  | { kind: 'message'; data: ChatMessage }
  | { kind: 'notice'; data: SystemNotice };

const EmptyState: React.FC = () => (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-center max-w-md px-4">
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/70 to-primary flex items-center justify-center mx-auto mb-4">
      <LayoutDashboard className="w-8 h-8 text-primary-foreground" />
    </div>
      <h3 className="text-lg font-medium text-foreground mb-2">
        Build your dashboard
      </h3>
      <p className="text-sm text-muted-foreground">
        Describe the charts and data you want to see. The AI will explore your
        data and create visualizations on the canvas.
      </p>
    </div>
  </div>
);

const SystemNoticeItem: React.FC<{ notice: SystemNotice }> = ({ notice }) => (
  <div className="flex items-center justify-center gap-2 py-1.5">
    <div className="h-px flex-1 bg-muted" />
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground px-2">
      {notice.icon === 'check' ? (
        <Check className="w-3 h-3 text-success" />
      ) : (
        <X className="w-3 h-3 text-destructive" />
      )}
      {notice.text}
    </span>
    <div className="h-px flex-1 bg-muted" />
  </div>
);

/**
 * Badge shown above user feedback messages to identify which block the feedback targets.
 */
const FeedbackBadge: React.FC<{ blockTitle: string }> = ({ blockTitle }) => (
  <div className="flex justify-end mb-1">
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent text-primary">
      <BarChart3 className="w-2.5 h-2.5" />
      Feedback on: {blockTitle}
    </span>
  </div>
);


function isFeedbackMessage(msg: ChatMessage): string | null {
  if (msg.role !== 'user' || !msg.attachments) return null;
  const ref = msg.attachments.find((a) => a.type === 'block_reference');
  if (!ref?.data?.block_title) return null;
  return ref.data.block_title as string;
}

const BuildChatPanel: React.FC = () => {
  const { state, sendMessage } = useDashboardBuild();
  const { chatMessages, systemNotices, isLLMResponding, liveSegments } = state;

  const endRef = useRef<HTMLDivElement>(null);

  const handleSend = useCallback(
    (content: string) => {
      sendMessage(content);
    },
    [sendMessage]
  );

  const visibleMessages = chatMessages.filter(
    (m) => m.role === 'user' || m.role === 'assistant'
  );

  const timeline: TimelineItem[] = useMemo(() => {
    const items: TimelineItem[] = [
      ...visibleMessages.map((m) => ({ kind: 'message' as const, data: m })),
      ...systemNotices.map((n) => ({ kind: 'notice' as const, data: n })),
    ];
    items.sort((a, b) => {
      const tA = a.kind === 'message' ? a.data.created_at : a.data.timestamp;
      const tB = b.kind === 'message' ? b.data.created_at : b.data.timestamp;
      return new Date(tA).getTime() - new Date(tB).getTime();
    });
    return items;
  }, [visibleMessages, systemNotices]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timeline, isLLMResponding]);

  const hasContent = timeline.length > 0 || isLLMResponding;

  return (
    <div className="flex flex-col h-full bg-background border-r border-border">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">
          Build Chat
        </h2>
        <p className="text-xs text-muted-foreground">
          Describe what you want — the AI builds it
        </p>
      </div>

      {/* Messages / Timeline */}
      {!hasContent ? (
        <EmptyState />
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
          {timeline.map((item) => {
            if (item.kind === 'notice') {
              return (
                <SystemNoticeItem key={item.data.id} notice={item.data} />
              );
            }

            const feedbackTitle = isFeedbackMessage(item.data);
            return (
              <div key={item.data.id}>
                {feedbackTitle && (
                  <FeedbackBadge blockTitle={feedbackTitle} />
                )}
                <MessageBubble message={item.data} />
              </div>
            );
          })}

          {/* Streaming response — interleaved segments */}
          {isLLMResponding && (
            <div className="flex gap-3 max-w-[85%] mr-auto">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary/70 to-primary flex items-center justify-center">
                <Bot className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-muted min-w-[60px] text-foreground">
                {liveSegments.length > 0 ? (
                  <SegmentRenderer segments={liveSegments} isStreaming />
                ) : (
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      )}

      {/* Input */}
      <MessageInput
        onSend={handleSend}
        disabled={isLLMResponding}
        placeholder="Describe a chart, give feedback, or ask a question..."
      />
    </div>
  );
};

export default BuildChatPanel;
